/**
 * Rental wizard telemetry — Phase 0.4 Slice 0 (John 2026-04-14)
 *
 * Minimal client-side event instrumentation. Goal: 14 napos adat-gyűjtés
 * dev4 + demo4 environmenteken az ADR-103 Addendum (1:N package per equipment)
 * G3 decision-gate-éhez. Ha a telemetria igazolja a multi-package use-case-t
 * (John kritérium: ≥15% rental-en package swap_attempt VAGY ≥2+ manual_added
 * package-en kívül), Phase 2 UI refactor elindul. Ha nem: 1:N marad off.
 *
 * Output sink:
 *   - Dev4 + demo4: structured `console.info(JSON)` — recoverable via browser
 *     DevTools export vagy sessionreplay logs.
 *   - Prod: same shape; később lecserélhető external analytics / OTEL sink-re
 *     (NEM ebben a fázisban — John ragaszkodott a minimum-viable slice-hoz).
 *
 * Invariánsok:
 *   - Soha nem loggolunk PII-t (partner-nevet, e-mailt, telefont). Csak ID-k
 *     és aggregált mezők. CLAUDE.md memory rules: "Soha ne tárolj PII-t".
 *   - Tenant-hatás: tenantId a logolt event-ben JWT-ből érkezik (ha elérhető).
 *     Jelenleg optional, későbbi Pino wrapper-hez készen állunk.
 */

/** Rental wizard telemetry event type — whitelisted names. */
export type RentalTelemetryEvent =
  | 'rental.package.attached'
  | 'rental.package.swap_attempt'
  | 'rental.accessory.manual_added'
  // Phase 2 (ADR-103 Addendum 2) — 1:N package_selection lifecycle events.
  // Tracks the new normalized packageSelections[] shape, not user behaviour.
  // Pseudonymous (no userId / partnerId) — DPIA-Light covered by ADR-104.
  | 'rental.package_selection.created'
  | 'rental.package_selection.removed'
  // Clone-banner hydrate (ADR-103 rental/02): cart+selections restored from last rental.
  | 'rental.clone.hydrated';

interface TelemetryPayload {
  // Required runtime fields (filled by caller).
  readonly rentalDraftId?: string | undefined;
  readonly equipmentId?: string | undefined;
  readonly packageId?: string | undefined;
  readonly previousPackageId?: string | undefined;
  readonly accessoryId?: string | undefined;
  // Phase 2 selection-lifecycle fields.
  readonly selectionId?: string | undefined;
  readonly removedReason?: 'user-action' | 'cart-cascade' | undefined;
  // Clone-hydrate counters (rental.clone.hydrated).
  readonly equipmentCount?: number | undefined;
  readonly packageCount?: number | undefined;
  // Optional tenant context (from JWT when available in future wiring).
  readonly tenantId?: string | undefined;
}

/**
 * Emit a structured rental wizard telemetry event.
 *
 * Shape (Pino-compatible):
 * ```json
 * {
 *   "level": 30,
 *   "time": "2026-04-14T10:00:00.000Z",
 *   "name": "rental-wizard-telemetry",
 *   "event": "rental.package.attached",
 *   "rentalDraftId": "uuid",
 *   "equipmentId": "uuid",
 *   "packageId": "uuid",
 *   "tenantId": "uuid"
 * }
 * ```
 */
export function logRentalEvent(event: RentalTelemetryEvent, payload: TelemetryPayload): void {
  const record = {
    level: 30, // Pino INFO
    time: new Date().toISOString(),
    name: 'rental-wizard-telemetry',
    event,
    ...payload,
  };
  // Using console.info — structured enough for grep-parsing in browser DevTools.
  // Future: swap for a proper client logger wrapper without changing call-sites.

  console.info(JSON.stringify(record));
}
